import { setLocalStorageItem, translate, useDynamicData } from '@avensia/excite';
import { createFlipYDecorator, SlideHeightAnimation } from '@avensia/excite-ui';
import { Position } from '@avensia/excite-ui';
import { styled } from '@glitz/react';
import * as React from 'react';
import { AdviceIcon, ChevronDownIcon, InfoIcon } from 'svenssons/shared/icons';
import { Flyout } from 'webapp/shared/flyout';
import { monochromeDark } from 'svenssons/shared/theme';
import { absoluteCenterDecorator } from 'webapp/decorators/center';
import { Theme } from 'webapp/theme';
import { ZIndex } from 'webapp/z-index';
import { StoreAvailability } from './StoreAvailability';
import { CloseIcon } from 'webapp/components/icons';
import { Button } from 'webapp/components/button';
import Link, { linkDecorator } from 'webapp/components/Link';

export const STORE_LOCALSTORAGE_KEY = 'selected_store';

type PropType = {
  open: boolean;
  setOpen: (open: boolean) => void;
  storesAvailability: NordicNest.Api.StoreAvailabilityApiModel[];
};

export function StoreAvailabilityFlyout({ open, setOpen, storesAvailability }: PropType) {
  const [data] = useDynamicData<NordicNest.Api.StoresApiModel>();

  const close = React.useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  if (!data?.stores) {
    return null;
  }

  return (
    <Flyout onClose={close} position={Position.Right} visible={open} zIndex={ZIndex.Overlay}>
      <Header>
        <FlyoutHeading>{translate(t => t.store.availableInStoreHeading)}</FlyoutHeading>
        <Button as={Theme.Transparent} onClick={close} square aria-label={translate(t => t.ariaLabelCloseButton)}>
          <CloseIcon />
        </Button>
      </Header>
      <FlyoutBody>
        {data?.stores.map(store => (
          <Store
            key={store.storePageUrl}
            storeAvailability={storesAvailability?.find(s => s.storeId === store.id)?.storeAvailability}
            closeFlyout={() => setOpen(false)}
            {...store}
          />
        ))}
      </FlyoutBody>
    </Flyout>
  );
}

function Store(
  store: NordicNest.Api.StoreApiModel & {
    storeAvailability?: NordicNest.Api.StoreAvailability;
    closeFlyout: () => void;
  },
) {
  const [open, setOpen] = React.useState(false);

  const onSelectStore = React.useCallback(
    (storeId: string) => {
      setLocalStorageItem(STORE_LOCALSTORAGE_KEY, storeId);
      store.closeFlyout();
    },
    [store],
  );

  return (
    <StoreContainer>
      <AccordionHeader onClick={() => setOpen(!open)}>
        <div>
          <StoreName>{store.name}</StoreName>
          <StoreAvailability storeAvailability={store.storeAvailability} />
        </div>
        <ChevronDownIcon css={createFlipYDecorator(open)} />
      </AccordionHeader>
      <HeightAnimation visible={open}>
        {/* TODO: only show OutOfStockInfo when stockStatus is out of stock */}
        {(!store.storeAvailability || store.storeAvailability === 'outOfStock') && (
          <OutOfStockInfo>
            <AdviceIcon />
            <Info>{translate(t => t.store.outOfStockInfo)}</Info>
          </OutOfStockInfo>
        )}
        <StoreInfo>
          <StoreAddress>
            <StoreInfoHeading>{store.locationDescription}</StoreInfoHeading>
            <div>
              {store.street} <br />
              {store.postalCode} {store.city}
            </div>
            <StorePage to={store.storePageUrl}>
              <StoreInfoIcon />
              <StoreInfoText>{translate(t => t.store.storePageLinkText)}</StoreInfoText>
            </StorePage>
          </StoreAddress>
          <OpeningHoursCountainer>
            <StoreInfoHeading>{translate(t => t.store.openHours)}</StoreInfoHeading>
            <OpenHours>
              {store.mondayToFriday?.label} {store.mondayToFriday?.timeSpan}
            </OpenHours>
            <OpenHours>
              {store.saturday?.label} {store.saturday?.timeSpan}
            </OpenHours>
            <OpenHours>
              {store.sunday?.label} {store.sunday?.timeSpan}
            </OpenHours>
            <PhonenNumber href={`tel:${store.phoneNumber}`}>{store.phoneNumber}</PhonenNumber>
          </OpeningHoursCountainer>
        </StoreInfo>
        <SelectStoreButton onClick={() => onSelectStore(store.id)}>
          {translate(t => t.productPage.selectStore)}
        </SelectStoreButton>
      </HeightAnimation>
    </StoreContainer>
  );
}

const HeightAnimation = styled(SlideHeightAnimation, {
  fontSize: t => t.sharedFontSize.s,
});

const Header = styled.div({
  height: 72,
  display: 'flex',
  justifyContent: 'flex-end',
  flexShrink: 0,
  position: 'relative',
  alignItems: 'center',
  backgroundColor: t => t.monochrome.color,
  padding: { right: t => t.sharedSpacing.xs },
  border: { bottom: { width: 1, style: 'solid', color: monochromeDark } },
});

const FlyoutHeading = styled.h2(absoluteCenterDecorator, {
  fontSize: t => t.sharedFontSize.m,
  fontWeight: 'normal',
  whiteSpace: 'nowrap',
  marginBottom: 0,
});

const StoreContainer = styled.div({
  borderBottom: { style: 'solid', width: 'thin', color: monochromeDark },
  padding: { x: t => t.sharedSpacing.l, y: t => t.sharedSpacing.m },
});

const StoreInfoHeading = styled.h4({
  fontSize: t => t.sharedFontSize.xs,
});

const StoreAddress = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const OpeningHoursCountainer = styled.div();
const OpenHours = styled.div();

const StoreName = styled.h3({
  textTransform: 'uppercase',
  fontFamily: 'charter',
  fontSize: t => t.sharedFontSize.s,
  marginBottom: t => t.sharedSpacing.xs,
});

const bottomLinkDecorator = styled({
  display: 'block',
  marginTop: t => t.sharedSpacing.m,
  marginBottom: 0,
  textDecoration: 'underline',
  ':hover': {
    textDecoration: 'underline',
  },
});

const StorePage = styled(Link, bottomLinkDecorator, {
  display: 'flex',
  alignItems: 'center',
  marginTop: 'auto',
});

const StoreInfoIcon = styled(InfoIcon, {
  fontSize: t => t.sharedFontSize.xxs,
  marginRight: t => t.sharedSpacing.xs,
});

const PhonenNumber = styled.a(linkDecorator, bottomLinkDecorator);

const StoreInfo = styled.div({
  margin: { top: t => t.sharedSpacing.m, bottom: t => t.sharedSpacing.xs },
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gridAutoRows: '1fr',
  fontSize: t => t.sharedFontSize.xs,
});

const AccordionHeader = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
});

const FlyoutBody = styled.div({ padding: { xy: t => t.sharedSpacing.l }, overflowY: 'auto' });

const OutOfStockInfo = styled.div({
  padding: { xy: t => t.sharedSpacing.m },
  backgroundColor: '#edece8',
  display: 'grid',
  gridTemplateColumns: '24px 1fr',
  alignItems: 'center',
  margin: { top: t => t.sharedSpacing.m, bottom: t => t.sharedSpacing.l },
});

const Info = styled.p({
  margin: { bottom: 0, left: t => t.sharedSpacing.m },
  fontSize: t => t.sharedFontSize.xxs,
});

const StoreInfoText = styled.div({
  fontSize: t => t.sharedFontSize.xs,
});

const SelectStoreButton = styled(Button, {
  width: '100%',
  marginTop: t => t.sharedSpacing.l,
});
